import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import './AuthPages.css';

export default function RegisterPage() {
  const { inviteCode: urlInvite } = useParams();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!urlInvite) {
      navigate('/login', { replace: true, state: { message: 'Registration is invite-only. You need a valid invite link to join.' } });
      return;
    }
    let cancelled = false;
    api.get(`/auth/validate-invite/${encodeURIComponent(urlInvite)}`)
      .then(data => {
        if (cancelled) return;
        if (data.valid) {
          setValid(true);
        } else {
          navigate('/login', { replace: true, state: { message: 'This invite link is invalid, expired, or has already been used.' } });
        }
      })
      .catch(() => {
        if (!cancelled) {
          navigate('/login', { replace: true, state: { message: 'Unable to validate invite code. Please try again later.' } });
        }
      })
      .finally(() => { if (!cancelled) setValidating(false); });
    return () => { cancelled = true; };
  }, [urlInvite, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await register(username, password, confirmPassword, urlInvite.trim().toUpperCase());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (validating || !valid) {
    return (
      <div className="auth-page">
        <div className="auth-card card" style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)' }}>Validating invite...</p>
        </div>
      </div>
    );
  }

  // Promo screen — show before registration form
  if (!showForm) {
    return (
      <div className="auth-page">
        <div className="auth-card card" style={{ textAlign: 'center', maxWidth: 440 }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>&#127807;</div>
          <h1 style={{ fontSize: 24, marginBottom: 4 }}>The Greenhouse Club</h1>
          <p style={{ color: 'var(--success)', fontSize: 14, fontStyle: 'italic', margin: '0 0 20px' }}>
            Where loyalty hits different.
          </p>

          <div style={{ textAlign: 'left', marginBottom: 20 }}>
            {[
              { icon: '&#127793;', title: 'Earn Bud Points', desc: 'Every pickup earns points toward free rewards.' },
              { icon: '&#127922;', title: 'Spin the Leaf', desc: 'Use spin tokens for a chance at bonus points and free nugs.' },
              { icon: '&#127942;', title: 'Unlock Achievements', desc: 'Hit milestones, earn badges, and level up your loyalty.' },
              { icon: '&#128230;', title: 'Pre-Order Pickups', desc: 'Reserve ahead of time — skip the wait.' },
              { icon: '&#127775;', title: 'Exclusive Rewards', desc: 'Redeem points in the Stash Shop for real product.' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', gap: 12, alignItems: 'flex-start',
                padding: '10px 0',
                borderBottom: i < 4 ? '1px solid var(--border)' : 'none',
              }}>
                <span style={{ fontSize: 24, flexShrink: 0, lineHeight: 1 }} dangerouslySetInnerHTML={{ __html: item.icon }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{item.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            background: 'rgba(56,161,105,0.1)', border: '1px solid rgba(56,161,105,0.3)',
            borderRadius: 'var(--radius-sm)', padding: '12px', marginBottom: 16,
            fontSize: 13, color: 'var(--success)',
          }}>
            You've been invited to join the community.
          </div>

          <button
            className="btn-primary auth-btn"
            onClick={() => setShowForm(true)}
            style={{ width: '100%', padding: '14px', fontSize: 16, fontWeight: 700 }}
          >
            Continue with Invite
          </button>

          <p className="auth-footer" style={{ marginTop: 16 }}>
            Already a member? <Link to="/login">Sign In</Link>
          </p>
        </div>
      </div>
    );
  }

  // Registration form
  return (
    <div className="auth-page">
      <div className="auth-card card">
        <h1>Join the Sesh</h1>
        <p className="auth-subtitle">Create your Greenhouse Club account</p>
        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Invite Code</label>
            <input
              value={urlInvite.toUpperCase()}
              readOnly
              style={{ letterSpacing: 2, fontWeight: 600, textTransform: 'uppercase' }}
            />
          </div>
          <div className="form-group">
            <label>Username</label>
            <input value={username} onChange={e => setUsername(e.target.value)} required autoFocus />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} />
          </div>
          <div className="form-group">
            <label>Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={6} />
          </div>
          <button type="submit" className="btn-primary auth-btn" disabled={loading}>
            {loading ? 'Creating account...' : 'Register'}
          </button>
        </form>
        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign In</Link>
        </p>
      </div>
    </div>
  );
}
